import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../api/client";
import { toast } from "sonner";
import Logo from "../../components/common/Logo";

/**
 * AcceptInvitePage — public route for accepting an organization invitation.
 * Route: /accept-invite/:token
 */
export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(null); // null = unknown, true = new user, false = existing user

  // Try to accept without password first (existing user case)
  const handleAcceptExisting = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/auth/accept-invite`, { token });
      toast.success("Welcome! You've joined the organization.");
      // Auto-login via cookie set by backend — redirect to dashboard
      window.location.href = "/";
    } catch (err) {
      const detail = err?.response?.data?.detail || "";
      if (detail.includes("Password is required")) {
        // New user — show password form
        setNeedsPassword(true);
      } else {
        setError(detail || "Failed to accept invitation");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleAcceptWithPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/accept-invite`, { token, password });
      toast.success("Account created! Welcome to the team.");
      window.location.href = "/";
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to accept invitation");
    } finally {
      setSubmitting(false);
    }
  };

  // Initial state: show accept button (tries existing user flow)
  if (needsPassword === null) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8">
          <div className="flex flex-col items-center mb-8">
            <Logo size={40} className="mb-4" />
            <h1 className="font-cabinet text-xl font-black tracking-tight text-slate-900 text-center">
              You've Been Invited
            </h1>
            <p className="text-sm text-slate-500 mt-2 text-center">
              Click below to accept the invitation and join the organization.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm mb-4">
              {error}
              {error.includes("expired") || error.includes("Invalid") ? (
                <div className="mt-2">
                  <a href="/admin/login" className="text-red-700 underline font-medium">
                    Back to login
                  </a>
                </div>
              ) : null}
            </div>
          )}

          <button
            onClick={handleAcceptExisting}
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Accepting...
              </>
            ) : (
              "Accept Invitation"
            )}
          </button>
        </div>
      </div>
    );
  }

  // New user: needs to set a password
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg border border-slate-200 shadow-sm p-8">
        <div className="flex flex-col items-center mb-6">
          <Logo size={40} className="mb-4" />
          <h1 className="font-cabinet text-xl font-black tracking-tight text-slate-900 text-center">
            Set Your Password
          </h1>
          <p className="text-sm text-slate-500 mt-2 text-center">
            Create a password to complete your account setup and join the organization.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md text-sm mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleAcceptWithPassword} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <p className="text-xs text-slate-500 mt-1">
              Must include uppercase, number, and special character.
            </p>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1">
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter your password"
              required
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Creating Account...
              </>
            ) : (
              "Create Account & Join"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
